'use client'

import { useEffect, useRef } from 'react'

import { styled } from 'styled-components'

import {
  AnalyticsEntityType,
  AnalyticsSource,
  Button,
  ButtonAsA,
  FormattedMessage,
} from '@b-stock/fe-core/bstock-react'
import {
  Breakpoints,
  Colors,
  Shadows,
  Typography,
} from '@b-stock/fe-core/design-system'

import AuctionCardSkeleton from '@/components/common/auctionCard/AuctionCardSkeleton'
import Carousel, {
  CarouselItemWrapper,
} from '@/components/common/carousel/Carousel'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import { useWinOpportunities } from '@/queries/winOpportunitiesQuery'

import WinOpportunitiesCard from './WinOpportunitiesCard'

const SCREEN_NAME = 'homepage'
const SKELETON_KEYS = ['sk-1', 'sk-2', 'sk-3', 'sk-4']
// Epic requirement: fewer than 4 eligible results renders the empty state
// rather than a sparse carousel.
const MIN_RESULTS_TO_SHOW = 4

const Inner = styled.div`
  width: 100%;
  ${Shadows.Bottom}
  border-radius: 1rem;
  background: ${Colors.Semantic.Neutral.white};
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  ${CarouselItemWrapper} {
    padding: 0 0.125rem;
  }
`

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const Title = styled.h2`
  ${Typography.Title2}
  margin: 0;
  color: ${Colors.Semantic.Neutral.light_black};

  @media ${Breakpoints.max.small} {
    ${Typography.Title3}
  }
`

const MatchCount = styled.span`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};
`

const Subtitle = styled.p`
  ${Typography.Body3}
  margin: 0;
  color: ${Colors.Semantic.Neutral.dark_grey};
`

const EmptyBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem 0;
  text-align: center;
`

const EmptyIcon = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background: ${Colors.Semantic.Neutral.medium_grey};
`

const EmptyTitle = styled.p`
  ${Typography.Body2Semibold}
  margin: 0;
  color: ${Colors.Semantic.Neutral.light_black};
`

export default function WinOpportunitiesModule() {
  const { listings, isLoading, isError, refetch } = useWinOpportunities()
  const { track } = useAnalytics()

  const impressionTrackedRef = useRef(false)
  const emptyImpressionTrackedRef = useRef(false)

  const filteredListings = listings.filter(
    (l) => !!l.auctionUrl && !!l.storefrontId
  )

  const hasResults =
    !isLoading && filteredListings.length >= MIN_RESULTS_TO_SHOW

  const hasFailed = isError && filteredListings.length === 0

  const isEmpty =
    !isLoading && !hasFailed && filteredListings.length < MIN_RESULTS_TO_SHOW

  useEffect(() => {
    if (hasResults && !impressionTrackedRef.current) {
      impressionTrackedRef.current = true
      track('win_opportunities_module_impression', {
        screenName: SCREEN_NAME,
        source: AnalyticsSource.HOME_PORTAL,
        entityType: AnalyticsEntityType.BUYER,
        listing_count: filteredListings.length,
        // TODO BP-2901: add buyer_account_id and personalization_applied once accessible
      })
    }
  }, [hasResults, filteredListings.length, track])

  useEffect(() => {
    if (isEmpty && !emptyImpressionTrackedRef.current) {
      emptyImpressionTrackedRef.current = true
      track('win_opportunities_empty_state_impression', {
        screenName: SCREEN_NAME,
        source: AnalyticsSource.HOME_PORTAL,
        entityType: AnalyticsEntityType.BUYER,
      })
    }
  }, [isEmpty, track])

  const cards = isLoading
    ? SKELETON_KEYS.map((key) => <AuctionCardSkeleton key={key} />)
    : filteredListings.map((listing, i) => (
        <WinOpportunitiesCard
          key={listing.listingId}
          listing={listing}
          position={i + 1}
        />
      ))

  return (
    <Inner role="region" aria-labelledby="win-opportunities-title">
      <TitleGroup>
        <Title id="win-opportunities-title">
          <FormattedMessage id="WinOpportunities.title" />
          {hasResults && (
            <MatchCount>
              {' '}
              &middot;{' '}
              <FormattedMessage
                id="WinOpportunities.matchedCount"
                values={{ count: filteredListings.length }}
              />
            </MatchCount>
          )}
        </Title>
        <Subtitle>
          <FormattedMessage id="WinOpportunities.subtitle" />
        </Subtitle>
      </TitleGroup>

      {hasFailed ? (
        <EmptyBody>
          <EmptyIcon />
          <EmptyTitle>
            <FormattedMessage id="WinOpportunities.errorState.title" />
          </EmptyTitle>
          <Button
            appearance="secondary"
            size="medium"
            onClick={() => refetch()}
          >
            <FormattedMessage id="WinOpportunities.errorState.cta" />
          </Button>
        </EmptyBody>
      ) : isEmpty ? (
        <EmptyBody>
          <EmptyIcon />
          <EmptyTitle>
            <FormattedMessage id="WinOpportunities.emptyState.title" />
          </EmptyTitle>
          <ButtonAsA href="/all-auctions" appearance="secondary" size="medium">
            <FormattedMessage id="WinOpportunities.emptyState.cta" />
          </ButtonAsA>
        </EmptyBody>
      ) : (
        <Carousel disabled={isLoading}>{cards}</Carousel>
      )}
    </Inner>
  )
}
